import { useCallback, useEffect, useRef } from 'react';
import { generalSettingStorage, progressStorage } from 'src/utils/StorageUtils';
import { defaultGeneralSettings } from '../../store/slices/generalSettingsSlice';
import { store } from '../../store/store';
import { useNote } from '../components/useNote';
import { useReminder } from '../components/useReminder';
import { useLearning } from '../data/useLearning';
import useProgress from '../data/useProgress';
import { useSocial } from '../data/useSocial';
import useUser from '../data/useUser';
import { useWordList } from '../data/useWordList';
import { useWordTranslate } from '../data/useWordTranslate';

const TWENTY_FOUR_HOURS = 10 * 60 * 1000;//  24 * 60 * 60 * 1000;//every day

export const useServiceWorker = () => {
    const { fetchUserData } = useUser(false);
    const { fetchLearningData } = useLearning(false);
    const { uploadProgress, uploadProgressAtomic } = useProgress();
    const { fetchTranslates } = useWordTranslate(false);
    const { fetchNoteData } = useNote(false);
    const { fetchSocialInfo } = useSocial(false);
    const { fetchAllWordListData } = useWordList();
    const { showReminderNotification } = useReminder();
    const messageHandlerSetUp = useRef(false);
    const workerRegistration = useRef<ServiceWorkerRegistration | null>(null);
    const lastReminderTime = useRef<string>('');

    const updateData = useCallback(async (token: string) => {
        try {
            await uploadProgressAtomic();
            const [user, learning] =
                await Promise.all([
                    fetchUserData(token!),
                    fetchLearningData(token!),
                ]);
            //  console.log('user', user);
            await Promise.all([
                fetchTranslates(user?.NativeLanguageCode),
                fetchNoteData(user?.UserId),
                fetchSocialInfo(user?.UserId!, user?.CountryCode),
                fetchAllWordListData(learning?.WordLists || []),
            ]);

            // Update last update timestamp
            const currentSettings = generalSettingStorage.get() || defaultGeneralSettings;
            generalSettingStorage.set({
                ...currentSettings,
                ServiceWorkerLastUpdate: Date.now().toString()
            });
        } catch (error) {
            console.error('Error updating auto user data:', error);
        }
    }, [fetchUserData, fetchLearningData, fetchNoteData,
        fetchAllWordListData, uploadProgressAtomic, fetchTranslates, fetchSocialInfo]);

    const checkLastUpdate = useCallback((currentUserInfo: any) => {
        const currentSettings = generalSettingStorage.get();
        const lastUpdate = currentSettings?.ServiceWorkerLastUpdate;
        const timestamp = lastUpdate ? parseInt(lastUpdate) : Date.now();
        const now = Date.now();

        // If we're using current timestamp, store it
        if (!lastUpdate) {
            const settings = currentSettings || defaultGeneralSettings;
            generalSettingStorage.set({
                ...settings,
                ServiceWorkerLastUpdate: timestamp.toString()
            });
        }

        // Only request update if 24 hours have passed
        if ((now - timestamp) >= TWENTY_FOUR_HOURS && currentUserInfo?.Token) {
            updateData(currentUserInfo.Token);
        }
    }, [updateData]);

    const checkReminder = useCallback((currentUserInfo: any) => {
        if (!currentUserInfo?.ReminderTimes) return;

        const now = new Date();
        const currentTime = now.toTimeString().split(' ')[0]; // Get current time in HH:MM:SS format
        const reminderTimes = currentUserInfo.ReminderTimes.split(',');
        const shouldShowReminder = reminderTimes.some((time: string) => {
            // Only show reminder if we haven't shown it for this time slot yet
            if (time === currentTime && time !== lastReminderTime.current) {
                lastReminderTime.current = time;
                return true;
            }
            return false;
        });

        if (shouldShowReminder) {
            showReminderNotification();
        }
    }, [showReminderNotification]);

    const setupMessageHandler = useCallback(() => {
        if (messageHandlerSetUp.current) return;

        const messageHandler = (event: MessageEvent) => {
            const { type } = event.data;
            const currentUserInfo = store.getState().user.userInfo;

            if (type === 'Check_SAVED_PROGRESS') {
                const progressFromStorage = progressStorage.get();
                if (progressFromStorage && progressFromStorage?.length > 0 && currentUserInfo?.Token) {
                    uploadProgress(progressFromStorage);
                }
            } else if (type === 'Check_LAST_UPDATE') {
                checkLastUpdate(currentUserInfo);
            } else if (type === 'CHECK_REMINDER') {
                checkReminder(currentUserInfo);
            }
        };

        navigator.serviceWorker.addEventListener('message', messageHandler);
        messageHandlerSetUp.current = true;
    }, [uploadProgress, checkLastUpdate, checkReminder]);

    const unregisterWorker = useCallback(async () => {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
            }
            workerRegistration.current = null;
            messageHandlerSetUp.current = false;
            window.location.reload();
        }
    }, []);

    const registerWorker = useCallback(async () => {
        if (!('serviceWorker' in navigator)) {
            console.warn('Service workers are not supported in this browser');
            return;
        }

        console.log('Attempting to register service worker...');
        try {
            const registration = await navigator.serviceWorker.register('/serviceWorker.js', {
                scope: '/'
            });
            console.log('Service worker registration successful:', registration);

            // Wait for the service worker to be active
            if (registration.active) {
                console.log('Service worker is already active');
                workerRegistration.current = registration;
                setupMessageHandler();
            } else {
                console.log('Waiting for service worker to activate...');
                registration.addEventListener('activate', () => {
                    console.log('Service worker activated');
                    workerRegistration.current = registration;
                    setupMessageHandler();
                });
            }

            // Check for updates
            registration.addEventListener('updatefound', () => {
                console.log('Service worker update found');
                const newWorker = registration.installing;
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        console.log('Service worker state changed:', newWorker.state);
                        if (newWorker.state === 'activated') {
                            workerRegistration.current = registration;
                            setupMessageHandler();
                        }
                    });
                }
            });

            // Handle service worker errors
            registration.addEventListener('error', (error) => {
                console.error('Service worker error:', error);
                // Attempt to unregister and re-register on error
                unregisterWorker().then(() => {
                    setTimeout(registerWorker, 5000); // Retry after 5 seconds
                });
            });

        } catch (error) {
            console.error('Service worker registration failed:', error);
            // Retry registration after a delay
            setTimeout(registerWorker, 5000);
        }
    }, [setupMessageHandler, unregisterWorker]);

    // Debug: Check service worker status on mount and periodically
    useEffect(() => {
        const checkWorkerStatus = async () => {
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.getRegistration('/serviceWorker.js');
                if (!registration) {
                    // If no registration found, try to register
                    registerWorker();
                } else if (!registration.active) {
                    // If registered but not active, try to re-register
                    unregisterWorker().then(registerWorker);
                } else {
                    workerRegistration.current = registration;
                    setupMessageHandler();
                }
            }
        };

        checkWorkerStatus();
        // Check status every 5 minutes
        const interval = setInterval(checkWorkerStatus, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [setupMessageHandler, registerWorker, unregisterWorker]);

    return { registerWorker, unregisterWorker };
};
